// Home (DESIGN §3): my queue across engagements (the engine's work.next actions addressed to me or to anyone on the team),
// approvals waiting (Inbox), engagements with the engine's before-release counts, risk flag and deadlines, agent activity now.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ActionDrawer, type ActionIntent } from "../app/ActionDrawer";
import { useApp } from "../app/context";
import { useDecisions } from "../app/decisions";
import { DecisionPanel } from "../app/DecisionPanel";
import { AgentChip, Button, Card, DataTable, EmptyState, LoadState, PageHeader, SourceList, StatusPill, type Column } from "../components";
import type { Action, AgentRun, EngagementHeader } from "../data/types";
import { actionPayload, addressedTo } from "../lib/commands";
import { isDecision } from "../lib/decisions";
import { ago, fmtDate, plural, titleCase } from "../lib/format";
import { useLoad, useRealtime } from "../lib/hooks";
import { daysText, deadlineName } from "./engagement/overviewParts";

export function HomePage() {
  const { ds, me, catalogue } = useApp();
  const actions = useLoad(() => ds.myActions(), [ds]);
  const engagements = useLoad(() => ds.engagements(), [ds]);
  const runs = useLoad(() => ds.activeRuns(), [ds]);
  useRealtime(ds, "actions", actions.reload);
  const { open: decisions } = useDecisions();
  const [intent, setIntent] = useState<ActionIntent | null>(null);
  const names = new Map((engagements.data ?? []).map((e) => [e.id, e.clientName]));

  const work = (actions.data ?? []).filter((a) => !isDecision(a));
  const cols: Array<Column<Action>> = [
    { id: "what", header: "What", cell: (a) => (
      <span className="flex flex-wrap items-center gap-1.5"><span>{a.what}</span>{!a.human && <AgentChip name="can do this" />}
        {a.sources.length > 0 && <span className="flex basis-full flex-wrap items-center gap-1 text-xs"><span className="text-muted">Rests on</span><SourceList sources={a.sources} /></span>}</span>), sortValue: (a) => a.what },
    { id: "eng", header: "Engagement", cell: (a) => (a.engagementId ? <Link className="hover:underline" to={`/e/${a.engagementId}`}>{names.get(a.engagementId) ?? a.engagementId}</Link> : <span className="text-muted">Firm</span>),
      sortValue: (a) => names.get(a.engagementId ?? "") ?? "" },
    { id: "phase", header: "Section", cell: (a) => <span className="text-muted">{titleCase(a.phase || a.kind)}</span>, sortValue: (a) => a.phase },
    { id: "act", header: <span className="sr-only">Act</span>, align: "right", cell: (a) => (
      <Button size="sm" onClick={(e) => { e.stopPropagation(); setIntent({ command: a.command, engagementId: a.engagementId, fixed: actionPayload(a, catalogue.get(a.command)), toFill: a.template?.toFill, title: a.what }); }}>Open form</Button>) },
  ];
  // Engagements with something the engine published to watch; ordered by the engine's own days to the next deadline, then counts.
  const watch = [...(engagements.data ?? [])].filter((e) => e.counts.beforeRelease > 0 || e.nextDeadline || (e.risk && (e.risk.significant > 0 || e.risk.fraud > 0)))
    .sort((a, b) => (a.nextDeadline?.days ?? Infinity) - (b.nextDeadline?.days ?? Infinity) || b.counts.beforeRelease - a.counts.beforeRelease);
  const proposals = decisions.filter((d) => d.proposal);

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Engine work queue" subtitle={`${me.personName} · the engine's queue of acts waiting on you across your engagements`} />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="min-w-0 space-y-4 lg:col-span-2">
          <Card title={`My queue${actions.data ? ` · ${work.length}` : ""}`} bodyClass="p-3">
            <DataTable rows={actions.data ? work : undefined} loading={actions.loading} error={actions.error} onRetry={actions.reload} columns={cols} rowKey={(a) => `${a.engagementId}:${a.key}`}
              filter={{ id: "who", label: "who", value: (a) => (!a.human ? "An agent can do it" : addressedTo(a.who, me.personName, me.role) === "me" ? "For you" : "Anyone on the team") }}
              empty="Nothing is waiting on you. The engine adds work here as the engagements move." caption="My queue" dense />
            <p className="mt-2 text-xs text-muted">From the engine's work queue: acts addressed to you by name or to anyone on the team. Decisions on proposals are in the Inbox.</p>
          </Card>
          {proposals.length > 0 && (
            <section aria-label="Agent proposals waiting on you" className="space-y-2">
              <h2 className="flex items-center justify-between text-sm font-semibold">Agent proposals waiting on you · {proposals.length}
                {proposals.length > 2 && <Link to="/inbox" className="text-xs font-normal text-brand hover:underline">All in the Inbox</Link>}</h2>
              {proposals.slice(0, 2).map((d) => <DecisionPanel key={d.id} d={d} />)}
            </section>
          )}
          <Card title="Agent activity now" bodyClass="p-3">
            <LoadState state={runs}>
              {(rs) => rs.length === 0 ? <EmptyState title="No agent is running.">Runs appear here while they work, with their last command.</EmptyState> : (
                <ul className="divide-y divide-line">{rs.map((r) => <RunRow key={r.id} r={r} client={names.get(r.engagementId) ?? r.engagementId} />)}</ul>
              )}
            </LoadState>
          </Card>
        </div>
        <div className="min-w-0 space-y-4">
          <Card title="Approvals waiting" action={<Link to="/inbox" className="text-xs text-brand hover:underline">Open Inbox</Link>}>
            <p className="text-3xl font-semibold tabular-nums">{decisions.length}</p>
            <p className="text-sm text-muted">{plural(decisions.length, "decision")} in your Inbox</p>
            <ul className="mt-3 space-y-1.5">
              {decisions.slice(0, 4).map((d) => (
                <li key={d.id} className="text-sm"><Link to={`/inbox?item=${encodeURIComponent(d.id)}`} className="hover:underline">{d.what}</Link>
                  {d.engagementName && <span className="block text-xs text-muted">{d.engagementName}</span>}</li>
              ))}
            </ul>
          </Card>
          <Card title="Engagements to watch">
            <LoadState state={engagements}>
              {() => watch.length === 0 ? <EmptyState title="No before-release items or deadlines published." /> : (
                <ul className="space-y-3">{watch.map((e) => <RiskRow key={e.id} e={e} />)}</ul>
              )}
            </LoadState>
          </Card>
        </div>
      </div>
      <ActionDrawer intent={intent} onClose={() => setIntent(null)} onSent={actions.reload} />
    </div>
  );
}

function RiskRow({ e }: { e: EngagementHeader }) {
  return (
    <li className="text-sm">
      <div className="flex items-center justify-between gap-2">
        <Link to={`/e/${e.id}/details/release`} className="font-medium hover:underline">{e.clientName}</Link>
        {e.nextDeadline ? <StatusPill state={e.nextDeadline.status} label={`${deadlineName(e.nextDeadline.what)}${daysText(e.nextDeadline) ? ` · ${daysText(e.nextDeadline)}` : ""}`} /> : <StatusPill state={e.phase} />}
      </div>
      <p className="text-xs text-muted">{plural(e.counts.beforeRelease, "before-release item")}{e.nextDeadline?.due ? ` · ${deadlineName(e.nextDeadline.what)} ${fmtDate(e.nextDeadline.due)}` : ""}
        {e.risk ? ` · ${e.risk.significant} significant, ${e.risk.fraud} fraud risk${e.risk.fraud === 1 ? "" : "s"}` : ""}</p>
    </li>
  );
}

function RunRow({ r, client }: { r: AgentRun; client: string }) {
  return (
    <li className="flex flex-wrap items-center gap-2 py-2 text-sm">
      <StatusPill state={r.state === "active" ? "running" : r.state} live={r.state === "active"} />
      <AgentChip name={r.roleCard} />
      <span className="min-w-0 basis-full sm:basis-0 sm:flex-1">{r.task}<span className="block text-xs text-muted">{client} · for {r.person ?? "—"} · started {ago(r.startedAt)}{r.lastCommand ? ` · last: ${r.lastCommand}` : ""}</span></span>
      <Link to={`/e/${r.engagementId}/details/agents`} className="text-xs text-brand hover:underline">Watch</Link>
    </li>
  );
}
